// สร้าง/แก้ไขข้อสอบ — ชื่อ ห้องที่ใช้ รายการข้อ เฉลย และคะแนนรายข้อ
//
// มีคำตอบของนักเรียนแล้ว = ล็อกโครง (จำนวนข้อ ชนิดข้อ จำนวนตัวเลือก) เพราะกระดาษที่พิมพ์ไป
// จัดตำแหน่งตามโครงนั้น แต่แก้เฉลยกับคะแนนได้ตลอด (server ตรวจซ้ำเสมอ ดู examModel.updateExam)
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Row } from 'react-bootstrap'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { API, LABEL_SETS, TYPE_LABELS, fmt, isObjective, keyLabel, parseKeyInput, readYearId, roomLabel, showError } from './examUtils'
import { MAX_BUBBLE_QUESTIONS } from './sheetLayout'

const groupKey = (t) => `${t.sub_subid}|${t.sub_semester}`

// ปุ่มเลือกเฉลยของหนึ่งข้อ — กด = เลือกคำตอบนั้นแทนของเดิม (แบบ radio)
// หลายคำตอบ (ตอบข้อใดก็ถูก): กด Ctrl/⌘ ค้าง หรือเปิดสวิตช์ multi / ✕ = ยกเลิกข้อ ทุกคนได้คะแนน
// เก็บค่าเป็นตัวอักษรแบบเดียวกับที่พิมพ์/วาง ('ก, ค' / 'A, C' / 'ถ' / '*') server ตรวจรูปแบบซ้ำเสมอ
// labels = ชุดตัวอักษรของข้อสอบ (LABEL_SETS) — ค่าเดิมที่เป็นอีกชุดยังอ่านออก เพราะอ่านเป็นรหัสก่อน
function KeyPicker({ type, value, choices, labels, multi, onChange }) {
  const options = type === 'truefalse'
    ? [{ code: 'T', label: labels.tf[0] }, { code: 'F', label: labels.tf[1] }]
    : labels.choice.slice(0, choices).map((label, i) => ({ code: String(i + 1), label }))
  const { codes, cancelled, invalid } = parseKeyInput(type, value, choices)
  const emit = (set) => onChange(options.filter((o) => set.has(o.code)).map((o) => o.label).join(', '))

  const pick = (code, e) => {
    if (multi || e.ctrlKey || e.metaKey) {
      const next = new Set(cancelled ? [] : codes)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      emit(next)
    } else {
      emit(new Set([code]))
    }
  }

  const missing = !cancelled && !codes.size
  return (
    <div className={`d-inline-flex flex-wrap align-items-center gap-1 rounded p-1 ${missing || invalid ? 'border border-danger' : ''}`}>
      {options.map((o) => {
        const on = !cancelled && codes.has(o.code)
        return (
          <Button key={o.code} size="sm" variant={on ? 'primary' : 'outline-secondary'}
            aria-pressed={on} style={{ minWidth: 34 }} onClick={(e) => pick(o.code, e)}>
            {o.label}
          </Button>
        )
      })}
      <Button size="sm" variant={cancelled ? 'warning' : 'outline-secondary'} aria-pressed={cancelled}
        title="ยกเลิกข้อ — ทุกคนได้คะแนน" aria-label="ยกเลิกข้อ"
        onClick={() => onChange(cancelled ? '' : '*')}>
        <i className="ri-close-line"></i>
      </Button>
      {invalid && <span className="text-danger fs-xs ms-1" title={value}>อ่านไม่ออก: {value}</span>}
    </div>
  )
}

// โครงที่กระดาษคำตอบจัดตำแหน่งตาม — เปลี่ยนแล้วกระดาษที่พิมพ์ไปแล้วใช้ไม่ได้
const shapeOf = (qs, choices) => `${choices}:${qs.map((q) => q.eq_type).join(',')}`

export default function ExamEditor() {
  const { examId } = useParams()
  const isNew = !examId
  const navigate = useNavigate()
  const { setLoading } = useLoading()
  const [, setSkin] = useState('')

  const [name, setName] = useState('')
  const [choices, setChoices] = useState(4)
  const [labelKey, setLabelKey] = useState('th')
  const labels = LABEL_SETS[labelKey]
  const [questions, setQuestions] = useState([])
  const [teachIds, setTeachIds] = useState(new Set())
  const [originalTeachIds, setOriginalTeachIds] = useState(new Set())
  const [teachings, setTeachings] = useState([])
  const [params] = useSearchParams()
  // ปีมาจากหน้ารายการ (?year_id=) ก่อน — มือถือกับคอมจำปีไว้คนละค่าได้ ถ้าอ่านจาก localStorage ตรงๆ
  // ครูจะเห็นรายการปีหนึ่ง แต่หน้าสร้างข้อสอบไปดึงห้องของอีกปี แล้วขึ้นว่าไม่มีห้อง
  const [yearId, setYearId] = useState(() => (isNew ? (/^\d+$/.test(params.get('year_id') || '') ? params.get('year_id') : readYearId()) : null))
  const [years, setYears] = useState([])
  const [responseCount, setResponseCount] = useState(0)
  const [roomResponses, setRoomResponses] = useState({})
  const [originalShape, setOriginalShape] = useState(null)

  const [addCount, setAddCount] = useState(10)
  const [addType, setAddType] = useState('choice')
  const [addPoints, setAddPoints] = useState(1)
  const [keyString, setKeyString] = useState('')
  const [multiKey, setMultiKey] = useState(false)

  const locked = responseCount > 0
  const shapeChanged = originalShape !== null && shapeOf(questions, choices) !== originalShape

  useEffect(() => {
    if (isNew) return
    setLoading(true)
    axiosInstance.get(`${API}/exams/${examId}`)
      .then((res) => {
        const { exam, questions: qs, rooms, response_count: count } = res.data
        setName(exam.exam_name)
        setChoices(Number(exam.exam_choices))
        const set = exam.exam_labels === 'en' ? 'en' : 'th'
        setLabelKey(set)
        setQuestions(qs.map((q) => ({ eq_type: q.eq_type, eq_key: keyLabel(q.eq_key, LABEL_SETS[set]), eq_points: q.eq_points })))
        setOriginalShape(shapeOf(qs, Number(exam.exam_choices)))
        const ids = new Set(rooms.map((r) => r.teach_id))
        setTeachIds(ids)
        setOriginalTeachIds(ids)
        setRoomResponses(Object.fromEntries(rooms.map((r) => [r.teach_id, r.response_count])))
        setResponseCount(count)
        setYearId(String(exam.exam_year_id))
      })
      .catch((err) => showError(err, 'โหลดข้อสอบไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [examId, isNew, setLoading])

  // ครูที่ยังไม่เคยเปิดหน้าอื่นของครูผู้สอนจะไม่มีปีการศึกษาค้างใน localStorage
  // ใช้ปีล่าสุดแทน ไม่งั้นรายการห้องว่างโดยไม่บอกว่าทำไม
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`)
      .then((res) => {
        setYears(res.data)
        // ยังไม่มีปี หรือปีที่ได้มาไม่มีในรายการ → ปีล่าสุด
        if (isNew && res.data.length) {
          setYearId((cur) => (cur && res.data.some((y) => String(y.year_id) === String(cur)) ? cur : String(res.data[0].year_id)))
        }
      })
      .catch(() => {})
  }, [isNew])

  useEffect(() => {
    if (!yearId) return
    axiosInstance.get(`${API}/exams/teachings`, { params: { year_id: yearId } })
      .then((res) => setTeachings(res.data))
      .catch((err) => showError(err, 'โหลดรายวิชาที่สอนไม่สำเร็จ'))
  }, [yearId])

  // วิชาเดียวกัน (รหัส + ภาค) หลายห้อง = ข้อสอบชุดเดียวกัน
  const groups = useMemo(() => {
    const map = new Map()
    for (const t of teachings) {
      if (!map.has(groupKey(t))) map.set(groupKey(t), { key: groupKey(t), first: t, rooms: [] })
      map.get(groupKey(t)).rooms.push(t)
    }
    return [...map.values()]
  }, [teachings])

  const selectedGroup = useMemo(() => {
    const first = teachings.find((t) => teachIds.has(t.teach_id))
    return first ? groupKey(first) : null
  }, [teachings, teachIds])

  const toggleRoom = (id) => {
    setTeachIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const objectiveCount = questions.filter(isObjective).length
  const total = questions.reduce((s, q) => s + (Number(q.eq_points) || 0), 0)

  const addQuestions = () => {
    const n = Math.max(1, Math.min(150, Number(addCount) || 0))
    const points = Number(addPoints) || 1
    setQuestions((prev) => [...prev, ...Array.from({ length: n }, () => ({ eq_type: addType, eq_key: '', eq_points: points }))])
  }

  const updateQuestion = (index, patch) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)))
  }

  const removeQuestion = (index) => setQuestions((prev) => prev.filter((_, i) => i !== index))

  // วางเฉลยต่อกันทีเดียว เช่น "13422ถผ" — ตัวอักษรละหนึ่งข้อ ไล่ใส่ข้อปรนัยตามลำดับ ข้ามข้ออัตนัย
  const applyKeyString = () => {
    const chars = [...keyString.replace(/\s+/g, '')]
    if (!chars.length) return
    let k = 0
    setQuestions((prev) => prev.map((q) => {
      if (!isObjective(q) || k >= chars.length) return q
      return { ...q, eq_key: chars[k++] }
    }))
    setKeyString('')
  }

  const save = async () => {
    if (!name.trim()) return Swal.fire({ icon: 'warning', title: 'กรุณาระบุชื่อข้อสอบ' })
    if (!teachIds.size) return Swal.fire({ icon: 'warning', title: 'กรุณาเลือกห้องที่ใช้ข้อสอบนี้' })
    if (!questions.length) return Swal.fire({ icon: 'warning', title: 'ยังไม่มีข้อสอบสักข้อ' })
    const payload = {
      exam_name: name,
      exam_choices: choices,
      exam_labels: labelKey,
      questions: questions.map((q) => ({ eq_type: q.eq_type, eq_key: q.eq_key, eq_points: q.eq_points })),
    }
    setLoading(true)
    try {
      if (isNew) {
        const res = await axiosInstance.post(`${API}/exams`, { ...payload, teach_ids: [...teachIds] })
        navigate(`/teaching/exams/${res.data.exam_id}`, { replace: true })
        return
      }
      await axiosInstance.put(`${API}/exams/${examId}`, payload)
      const roomsChanged = teachIds.size !== originalTeachIds.size || [...teachIds].some((id) => !originalTeachIds.has(id))
      if (roomsChanged) await axiosInstance.put(`${API}/exams/${examId}/rooms`, { teach_ids: [...teachIds] })
      navigate(`/teaching/exams/${examId}`)
    } catch (err) {
      showError(err, 'บันทึกไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  const remove = async () => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'ลบข้อสอบชุดนี้?',
      text: responseCount
        ? `คำตอบของนักเรียน ${responseCount} คนจะหายไปด้วย (คะแนนที่ส่งเข้าตารางคะแนนแล้วยังอยู่)`
        : 'ลบแล้วกู้คืนไม่ได้',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    })
    if (!ok.isConfirmed) return
    setLoading(true)
    try {
      await axiosInstance.delete(`${API}/exams/${examId}`)
      navigate('/teaching/exams', { replace: true })
    } catch (err) {
      showError(err, 'ลบไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <div className="me-auto">
            <Link to={isNew ? '/teaching/exams' : `/teaching/exams/${examId}`} className="fs-sm">
              <i className="ri-arrow-left-line"></i> กลับ
            </Link>
            <h4 className="main-title mb-0">{isNew ? 'สร้างข้อสอบ' : 'แก้ไขข้อสอบ'}</h4>
          </div>
          {!isNew && (
            <Button variant="outline-danger" size="sm" onClick={remove}>
              <i className="ri-delete-bin-line"></i> ลบข้อสอบ
            </Button>
          )}
          <Button size="sm" onClick={save}>
            <i className="ri-save-line"></i> บันทึก
          </Button>
        </div>

        {locked && (
          <Alert variant="info" className="fs-sm">
            มีคำตอบของนักเรียนแล้ว {responseCount} คน — แก้เฉลยและคะแนนรายข้อได้ (ผลสอบคิดใหม่ให้ทันที)
            แต่เพิ่ม/ลบข้อ เปลี่ยนชนิดข้อ หรือจำนวนตัวเลือกไม่ได้ เพราะกระดาษที่พิมพ์ไปจัดตำแหน่งตามโครงเดิม
          </Alert>
        )}

        {!locked && shapeChanged && (
          <Alert variant="warning" className="fs-sm">
            จำนวนข้อ ชนิดข้อ หรือจำนวนตัวเลือกเปลี่ยนจากเดิม — ถ้าพิมพ์กระดาษคำตอบไปแล้ว ต้องพิมพ์ใหม่ทั้งหมด
            เพราะตำแหน่งข้อบนกระดาษเดิมจะไม่ตรงกับข้อสอบชุดนี้
          </Alert>
        )}

        <Row className="g-3">
          <Col lg={4}>
            {/* card-one ตั้ง height: 100% ไว้ — สองการ์ดซ้อนในคอลัมน์เดียวต้อง h-auto ไม่งั้นบนมือถือการ์ดที่สองล้นไปอยู่ใต้การ์ดข้อสอบ */}
            <Card className="card-one h-auto mb-3">
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>ชื่อข้อสอบ</Form.Label>
                  <Form.Control value={name} maxLength={200} placeholder="เช่น สอบกลางภาค" onChange={(e) => setName(e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>ปีการศึกษา</Form.Label>
                  <Form.Select value={yearId || ''} disabled={!isNew}
                    onChange={(e) => { setYearId(e.target.value); setTeachIds(new Set()) }}>
                    {years.map((y) => <option key={y.year_id} value={y.year_id}>{y.year_year}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>ตัวเลือกของข้อปรนัย</Form.Label>
                  <Form.Select value={choices} disabled={locked} onChange={(e) => setChoices(Number(e.target.value))}>
                    <option value={4}>4 ตัวเลือก ({labels.choice[0]}–{labels.choice[3]})</option>
                    <option value={5}>5 ตัวเลือก ({labels.choice[0]}–{labels.choice[4]})</option>
                  </Form.Select>
                </Form.Group>
                {/* ป้ายบนช่องฝนเท่านั้น — รหัสที่เก็บเหมือนกันทุกชุด จึงเปลี่ยนได้แม้มีคำตอบแล้ว */}
                <Form.Group className="mt-3">
                  <Form.Label>ตัวอักษรบนกระดาษคำตอบ</Form.Label>
                  <Form.Select value={labelKey} onChange={(e) => setLabelKey(e.target.value)}>
                    <option value="th">ก ข ค ง · ใช่/ไม่ใช่ ถ ผ</option>
                    <option value="en">A B C D · True/False T F (วิชาภาษาอังกฤษ)</option>
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="card-one h-auto">
              <Card.Header><Card.Title as="h6" className="mb-0">ห้องที่ใช้ข้อสอบนี้</Card.Title></Card.Header>
              <Card.Body>
                {groups.length === 0 && (
                  <div className="text-secondary fs-sm">
                    ไม่พบวิชาที่คุณสอนในปีการศึกษา {years.find((y) => String(y.year_id) === String(yearId))?.year_year || ''}
                    {isNew && ' — ลองเลือกปีการศึกษาอื่นด้านบน'}
                  </div>
                )}
                {groups.map((g) => {
                  const disabled = selectedGroup && selectedGroup !== g.key
                  return (
                    <div key={g.key} className={`mb-3 ${disabled ? 'opacity-50' : ''}`}>
                      <div className="fw-semibold fs-sm">
                        {g.first.sub_subid} {g.first.sub_subname}
                        <span className="text-secondary fw-normal"> · ภาค {g.first.sub_semester}</span>
                      </div>
                      <div className="d-flex flex-wrap gap-3 mt-1">
                        {g.rooms.map((r) => {
                          const used = roomResponses[r.teach_id] > 0
                          return (
                            <Form.Check
                              key={r.teach_id}
                              id={`room-${r.teach_id}`}
                              label={`${roomLabel(r)}${used ? ` (${roomResponses[r.teach_id]} คน)` : ''}`}
                              checked={teachIds.has(r.teach_id)}
                              disabled={disabled || (used && teachIds.has(r.teach_id))}
                              onChange={() => toggleRoom(r.teach_id)}
                            />
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
                <div className="fs-xs text-secondary">
                  ข้อสอบชุดหนึ่งใช้ได้หลายห้องของวิชาเดียวกัน ห้องที่มีคำตอบแล้วเอาออกไม่ได้
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8}>
            <Card className="card-one">
              <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <Card.Title as="h6" className="mb-0 me-auto">
                  ข้อสอบ {questions.length} ข้อ · เต็ม {fmt(total)} คะแนน
                </Card.Title>
                {objectiveCount > MAX_BUBBLE_QUESTIONS && (
                  <Badge bg="danger">ข้อปรนัยเกิน {MAX_BUBBLE_QUESTIONS} ข้อ ไม่พอหนึ่งแผ่น</Badge>
                )}
              </Card.Header>
              <Card.Body>
                {!locked && (
                  <InputGroup size="sm" className="mb-3 flex-nowrap">
                    <InputGroup.Text>เพิ่ม</InputGroup.Text>
                    <Form.Control type="number" min={1} max={150} value={addCount} style={{ maxWidth: 70 }}
                      onChange={(e) => setAddCount(e.target.value)} />
                    <InputGroup.Text>ข้อ</InputGroup.Text>
                    <Form.Select value={addType} style={{ maxWidth: 130 }} onChange={(e) => setAddType(e.target.value)}>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </Form.Select>
                    <InputGroup.Text>ข้อละ</InputGroup.Text>
                    <Form.Control type="number" min={0.5} step={0.5} value={addPoints} style={{ maxWidth: 70 }}
                      onChange={(e) => setAddPoints(e.target.value)} />
                    <InputGroup.Text>คะแนน</InputGroup.Text>
                    <Button onClick={addQuestions}><i className="ri-add-line"></i></Button>
                  </InputGroup>
                )}

                {objectiveCount > 0 && (
                  <InputGroup size="sm" className="mb-3">
                    <InputGroup.Text>วางเฉลยต่อกัน</InputGroup.Text>
                    <Form.Control
                      value={keyString}
                      placeholder={labelKey === 'en'
                        ? 'เช่น 13422 หรือ ACDBB · True/False ใช้ T F — ไล่ใส่ข้อปรนัยตามลำดับ'
                        : 'เช่น 13422 หรือ กคงขข · ใช่/ไม่ใช่ใช้ ถ ผ — ไล่ใส่ข้อปรนัยตามลำดับ'}
                      onChange={(e) => setKeyString(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && applyKeyString()}
                    />
                    <Button variant="outline-primary" onClick={applyKeyString}>ใส่เฉลย</Button>
                  </InputGroup>
                )}
                {objectiveCount > 0 && (
                  <Form.Check type="switch" id="multi-key" className="mb-2 fs-sm"
                    label="เลือกได้หลายคำตอบต่อข้อ (ตอบข้อใดก็ถูก) — บนคอมกด Ctrl ค้างแล้วคลิกแทนได้"
                    checked={multiKey} onChange={(e) => setMultiKey(e.target.checked)} />
                )}

                {/* กริดแทนตาราง: จอแคบ แถวบน = ข้อ/ชนิด/คะแนน แถวล่าง = ปุ่มเฉลยเต็มความกว้าง
                    (ในตาราง 5 คอลัมน์ ช่องเฉลยถูกบีบจนปุ่มเรียงลงทีละปุ่ม) */}
                <div className="overflow-auto" style={{ maxHeight: '65vh' }}>
                  <Row className="g-2 fw-semibold fs-sm border-bottom pb-1 mx-0 d-none d-md-flex sticky-top bg-body">
                    <Col xs="auto" style={{ width: 44 }}>ข้อ</Col>
                    <Col md={3}>ชนิด</Col>
                    <Col md>เฉลย</Col>
                    <Col xs="auto" style={{ width: 84 }}>คะแนน</Col>
                    {!locked && <Col xs="auto" style={{ width: 32 }}></Col>}
                  </Row>
                  {questions.map((q, i) => (
                    <Row key={i} className="g-2 align-items-center border-bottom py-2 mx-0">
                      <Col xs="auto" className="fw-semibold" style={{ width: 44 }}>{i + 1}</Col>
                      <Col xs md={3}>
                        <Form.Select size="sm" value={q.eq_type} disabled={locked} aria-label={`ชนิดข้อ ${i + 1}`}
                          onChange={(e) => updateQuestion(i, { eq_type: e.target.value, eq_key: '' })}>
                          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </Form.Select>
                      </Col>
                      <Col xs={12} md className="order-last order-md-0">
                        {q.eq_type === 'written' ? (
                          <span className="text-secondary fs-sm">ครูกรอกคะแนนเอง</span>
                        ) : (
                          <KeyPicker type={q.eq_type} value={q.eq_key} choices={choices} labels={labels} multi={multiKey}
                            onChange={(eq_key) => updateQuestion(i, { eq_key })} />
                        )}
                      </Col>
                      <Col xs="auto" style={{ width: 84 }}>
                        <Form.Control size="sm" type="number" min={0.5} step={0.5} value={q.eq_points}
                          aria-label={`คะแนนข้อ ${i + 1}`}
                          onChange={(e) => updateQuestion(i, { eq_points: e.target.value })} />
                      </Col>
                      {!locked && (
                        <Col xs="auto" style={{ width: 32 }}>
                          <Button size="sm" variant="link" className="text-danger p-0" aria-label={`ลบข้อ ${i + 1}`}
                            onClick={() => removeQuestion(i)}>
                            <i className="ri-close-line"></i>
                          </Button>
                        </Col>
                      )}
                    </Row>
                  ))}
                </div>
                <div className="fs-xs text-secondary mt-2">
                  กดปุ่มเพื่อเลือกเฉลย · หลายคำตอบ (ตอบข้อใดก็ถูก) เปิดสวิตช์ด้านบนหรือกด Ctrl ค้างแล้วคลิก ·
                  ปุ่ม <i className="ri-close-line"></i> ยกเลิกข้อ ทุกคนได้คะแนน · ข้อที่ยังไม่มีเฉลย (กรอบแดง) จะยังไม่ถูกคิดคะแนนให้ใคร
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </>
  )
}
